// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.

import {
    Body1,
    Body1Strong,
    Body2,
    Image,
    ImageProps,
    Link,
    LinkProps,
    Table,
    TableBody,
    TableBodyProps,
    TableCell,
    TableCellProps,
    TableHeader,
    TableHeaderCell,
    TableHeaderCellProps,
    TableHeaderProps,
    TableProps,
    TableRow,
    TableRowProps,
    TextPresetProps,
    Title2,
    Title3,
} from '@fluentui/react-components';
import { assert, isAbsoluteUrl, isNonEmptyString } from '@microsoft/vscodeedu-api';
import { Root as hastRoot } from 'hast';
import { Root as mdastRoot } from 'mdast';
import React, { ReactElement, useEffect, useState } from 'react';
import * as production from 'react/jsx-runtime';
import rehypeRaw from 'rehype-raw';
import rehypeReact, { Options } from 'rehype-react';
import rehypeSanitize from 'rehype-sanitize';
import remarkGfm from 'remark-gfm';
import remarkParse from 'remark-parse';
import remarkRehype from 'remark-rehype';
import { Processor, unified } from 'unified';
import { visit } from 'unist-util-visit';
import { timeAsync } from './performance';
import { getFetchUrl, getRouteUrl } from './routing';

export class MarkdownRenderer {
    protected remarkProcessor?: Processor<mdastRoot, mdastRoot, hastRoot, hastRoot, JSX.Element>;
    private rendererLoad: Promise<void>;

    constructor() {
        // Trigger the initial creation of our renderer
        this.rendererLoad = this.createMarkdownRenderer();
    }

    public async render(markdown: string): Promise<ReactElement> {
        await this.rendererLoad;
        assert.isDefined(this.remarkProcessor, 'Failed to create markdown loader');
        const result = await this.remarkProcessor.process(markdown);
        return result.result as ReactElement;
    }

    protected async createMarkdownRenderer() {
        this.remarkProcessor = createProcessor();
    }
}

export class UnsafeMarkdownRenderer extends MarkdownRenderer {
    protected async createMarkdownRenderer() {
        this.remarkProcessor = createUnsafeProcessor();
    }
}

export const rehypeReactOptions = {
    a: (props: LinkProps) => <Link {...props} />,
    p: (props: TextPresetProps) => (
        <p>
            <Body1 {...props} />
        </p>
    ),
    strong: (props: TextPresetProps) => <Body1Strong {...props} />,
    h1: (props: TextPresetProps) => (
        <h1>
            <Title2 {...props} />
        </h1>
    ),
    h2: (props: TextPresetProps) => (
        <h2>
            <Title3 {...props} />
        </h2>
    ),
    h3: (props: TextPresetProps) => (
        <h3>
            <Body2 {...props} />
        </h3>
    ),
    h4: (props: TextPresetProps) => (
        <h4>
            <Body1 {...props} />
        </h4>
    ),
    table: (props: TableProps) => <Table {...props} />,
    thead: (props: TableHeaderProps) => <TableHeader {...props} />,
    tr: (props: TableRowProps) => <TableRow {...props} />,
    tbody: (props: TableBodyProps) => <TableBody {...props} />,
    th: (props: TableHeaderCellProps) => <TableHeaderCell {...props} />,
    td: (props: TableCellProps) => <TableCell {...props} />,
    img: (props: ImageProps) => <Image {...props} style={{ maxWidth: props.width }} width={'100%'} height={'auto'} />,
};

function createProcessor() {
    const options = { ...production, components: rehypeReactOptions } as Options;
    return unified()
        .use(remarkParse)
        .use(remarkGfm)
        .use(remarkRehype, { allowDangerousHtml: true })
        .use(rehypeSanitize)
        .use(rehypeReact, options);
}

function createUnsafeProcessor() {
    const updateLinks = () => (root: hastRoot) =>
        visit(root, 'element', (node) => {
            if (node.tagName === 'img') {
                node.properties.src = getFetchUrl(node.properties.src as string);
            } else if (node.tagName === 'a') {
                const href = node.properties.href as string;
                if (!isAbsoluteUrl(href)) {
                    node.properties.href = getRouteUrl(href);
                } else if (!href.startsWith(window.location.origin)) {
                    node.properties.target = '_blank';
                }
            }
        });

    const options = { ...production, components: rehypeReactOptions } as Options;
    return unified()
        .use(remarkParse)
        .use(remarkGfm)
        .use(remarkRehype, { allowDangerousHtml: true })
        .use(rehypeRaw)
        .use(updateLinks)
        .use(rehypeReact, options);
}

export function useRenderer(markdown: string | undefined) {
    return useRendererCore(markdown, MarkdownRenderer);
}

export function useUnsafeRenderer(markdown: string | undefined) {
    return useRendererCore(markdown, UnsafeMarkdownRenderer);
}

function useRendererCore(markdown: string | undefined, rendererProto: { new (): MarkdownRenderer }) {
    const [content, setContent] = useState<ReactElement>();

    useEffect(() => {
        if (!isNonEmptyString(markdown)) {
            setContent(<></>);
            return;
        }
        (async () => {
            const rendered = await timeAsync('render-markdown', async () => {
                const renderer = new rendererProto();
                return await renderer.render(markdown);
            });
            setContent(rendered);
        })();
    }, [markdown, rendererProto]);

    return content;
}

export function useFetched(src: string | undefined) {
    const [fetched, setFetched] = useState<string>();

    useEffect(() => {
        (async () => {
            const result = await timeAsync('fetch-markdown', async () => {
                if (!isNonEmptyString(src)) {
                    return undefined;
                }
                const response = await fetch(getFetchUrl(src));
                return await response.text();
            });

            setFetched(await result);
        })();
    }, [src]);

    return fetched;
}
